/* ============================================================================
   ELIO — PDP colorway swap
   ---------------------------------------------------------------------------
   The six colorways are six separate products, so every swatch click is a full
   navigation. This intercepts that click, fetches the target product page,
   swaps the section in place, re-runs the scripts that need it, and syncs the
   URL and head metadata so the result is indistinguishable from a real page
   load to Shopify, to search engines, and to the pixel.

   Design constraints this respects:
     - Links stay real <a href> elements. Middle-click, cmd-click, right-click,
       and crawlers are untouched. Only a plain left click is intercepted.
     - Any failure falls back to normal navigation. There is no state in which
       a shopper cannot reach the other colorway.
     - Only scripts marked data-elio-rerun are re-executed. Blocks that bind
       window-level listeners inside DOMContentLoaded are deliberately skipped:
       that event never fires again, so re-running them is a no-op, and their
       handlers query the DOM lazily and survive the swap intact.
     - Meta standard ViewContent is not fired manually on in-page color swaps.
       The official Shopify Facebook & Instagram app pixel owns standard Meta
       events so browser/server delivery and deduplication stay aligned.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-elio-pdp-swap]');
  if (!root) return;

  var SECTION_ID = root.getAttribute('data-elio-pdp-swap');
  var WRAP_ID = 'shopify-section-' + SECTION_ID;
  var CAROUSEL_INIT = 'initElioPdpCarousel' + SECTION_ID.replace(/-/g, '');

  if (!('fetch' in window) || !('AbortController' in window) ||
      !window.history || !history.pushState) return;

  var cache = Object.create(null);
  var pending = Object.create(null);
  var inflight = null;
  var busy = false;
  /* Guarded: an exception thrown during init would disable the swap silently
     and leave no fallback, so nothing here is allowed to throw. */
  var reduceMotion = false;
  try {
    reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) {}

  /* --- helpers ------------------------------------------------------------ */

  function wrapper() { return document.getElementById(WRAP_ID); }

  /* Nothing on the success path is allowed to throw: a rejection here would be
     caught below and turned into a full page load, silently undoing the swap. */
  function nextFrame(fn) {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(fn);
    } else {
      setTimeout(fn, 16);
    }
  }

  /* Assigning location.href to the URL already in the address bar is a no-op,
     so fall through to a reload when the two match. */
  function hardNavigate(url) {
    if (stripQuery(window.location.pathname) === stripQuery(url)) {
      window.location.reload();
    } else {
      window.location.href = url;
    }
  }

  function stripQuery(url) { return url.split('?')[0].split('#')[0]; }

  function selectedSize() {
    var el = document.querySelector('#' + WRAP_ID + ' .elio-pdp__size-input:checked');
    return el ? el.value : '';
  }

  function activeColorKey() {
    var el = document.querySelector('#' + WRAP_ID + ' [data-color-link][aria-current="page"]');
    return el ? el.getAttribute('data-base-url') : '';
  }

  /* --- fetch -------------------------------------------------------------- */

  /* Section-only render. Roughly half the bytes of the full document, because
     the head data the swap actually needs (title, canonical, og image) is
     carried inside the section itself via [data-elio-swap-meta]. */
  function sectionUrl(path) {
    return stripQuery(path) + '?section_id=' + encodeURIComponent(SECTION_ID);
  }

  function parse(html, key) {
    var frag = document.createElement('div');
    frag.innerHTML = html;
    var next = frag.querySelector('[data-elio-pdp-swap]') || frag.firstElementChild;
    if (!next) throw new Error('section markup not found in response');

    var meta = null;
    var mnode = frag.querySelector('[data-elio-swap-meta]');
    if (mnode) { try { meta = JSON.parse(mnode.textContent || 'null'); } catch (e) { meta = null; } }

    var entry = { html: html, meta: meta };
    cache[key] = entry;

    /* Decode the hero image now. HTML arriving instantly is not the same as the
       colour changing instantly — the image is what the eye is waiting for. */
    if (meta && meta.preload) {
      var img = new Image();
      img.decoding = 'async';
      img.src = meta.preload;
      entry.image = img;
    }
    return entry;
  }

  function load(url) {
    var key = stripQuery(url);
    if (cache[key]) return Promise.resolve(cache[key]);

    if (inflight) { try { inflight.abort(); } catch (e) {} }
    inflight = new AbortController();

    return fetch(sectionUrl(key), {
      signal: inflight.signal,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) { return parse(html, key); });
  }

  function prefetch(url) {
    var key = stripQuery(url);
    if (cache[key] || pending[key]) return;
    pending[key] = true;
    fetch(sectionUrl(key), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) { if (html && !cache[key]) parse(html, key); })
      .catch(function () { /* best effort */ })
      .finally(function () { delete pending[key]; });
  }

  /* ---- eager warming ------------------------------------------------------
     Hover and touchstart buy 100-150ms, which is not enough on a cold mobile
     connection. Every colourway is fetched during idle time after first paint
     instead, so a click is a memory read and the network is out of the loop.
     Six section renders is real data, so this is gated on connection quality
     and skipped entirely under Save-Data. */
  function warmAll() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      if (conn.saveData) return;
      if (/(^|\b)(slow-2g|2g)$/.test(conn.effectiveType || '')) return;
    }

    var links = document.querySelectorAll('#' + WRAP_ID + ' [data-color-link]');
    var urls = [];
    for (var i = 0; i < links.length; i++) {
      var b = links[i].getAttribute('data-base-url');
      if (b && b !== activeColorKey() && urls.indexOf(b) === -1) urls.push(b);
    }

    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 120); };
    /* Staggered so warming never competes with the current page finishing, but
       tightly: at 180ms apart the last colourway landed ~900ms after load, which
       is late enough for a fast shopper to out-click it. 60ms puts all six in
       cache inside ~360ms while still sequencing the requests. */
    urls.forEach(function (u, i) {
      idle(function () { setTimeout(function () { prefetch(u); }, i * 60); },
           { timeout: 2000 });
    });
  }

  /* --- head metadata ------------------------------------------------------ */

  function syncHead(meta, url) {
    if (!meta) return;

    if (meta.title) document.title = meta.title;

    function set(selector, attr, value) {
      if (!value) return;
      var el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    }
    set('link[rel="canonical"]', 'href', meta.canonical || url);
    set('meta[name="description"]', 'content', meta.description);
    set('meta[property="og:title"]', 'content', meta.title);
    set('meta[property="og:description"]', 'content', meta.description);
    set('meta[property="og:image"]', 'content', meta.image);
    set('meta[property="og:url"]', 'content', meta.canonical || url);
    set('meta[name="twitter:title"]', 'content', meta.title);
    set('meta[name="twitter:image"]', 'content', meta.image);

    /* Product JSON-LD is injected by Shopify into content_for_header and is not
       reachable from a section render, so it is not updated here. Crawlers hit
       the six real URLs directly and get correct server-rendered structured
       data; only the live tab keeps the entry colourway's block. */
  }

  /* --- script re-execution ------------------------------------------------ */

  function rerunScripts(scope) {
    var scripts = scope.querySelectorAll('script[data-elio-rerun]');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var fresh = document.createElement('script');
      for (var a = 0; a < old.attributes.length; a++) {
        fresh.setAttribute(old.attributes[a].name, old.attributes[a].value);
      }
      fresh.appendChild(document.createTextNode(old.textContent));
      old.parentNode.replaceChild(fresh, old);
    }
  }

  function reinit(scope) {
    // Carousel exposes a named global init; calling it directly avoids adding
    // a duplicate shopify:section:load listener on every swap.
    if (typeof window[CAROUSEL_INIT] === 'function') {
      try { window[CAROUSEL_INIT](); } catch (e) {}
    }
    rerunScripts(scope);

    // External per-section widgets that key off DOM presence.
    if (window.elioShipStrip && typeof window.elioShipStrip.refresh === 'function') {
      try { window.elioShipStrip.refresh(); } catch (e) {}
    }
    if (window.Shopify && window.Shopify.PaymentButton) {
      try { window.Shopify.PaymentButton.init(); } catch (e) {}
    }
    // Let the accessibility layer pick up anything dialog-shaped in the new DOM.
    if (window.elioA11y && typeof window.elioA11y.scan === 'function') {
      try { window.elioA11y.scan(scope); } catch (e) {}
    }
  }

  /* --- analytics ---------------------------------------------------------- */

  function trackView(scope) {
    var node = scope.querySelector('[data-product-variants]');
    if (!node) return;
    var variants;
    try { variants = JSON.parse(node.textContent || '[]'); } catch (e) { return; }
    if (!variants.length) return;

    var size = selectedSize();
    var v = null;
    for (var i = 0; i < variants.length; i++) {
      if (!size || variants[i].option1 === size) { v = variants[i]; break; }
    }
    v = v || variants[0];

    var title = scope.querySelector('.elio-pdp__title');
    var name = title ? title.textContent.trim() : document.title;
    var value = (v.price || 0) / 100;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'view_item', {
          currency: currency,
          value: value,
          items: [{ item_id: String(v.id), item_name: name, price: value }]
        });
      } catch (e) {}
    }

    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      try { window.dataLayer.push({ event: 'view_item', item_id: String(v.id), item_name: name, value: value, currency: currency }); } catch (e) {}
    }
  }

  /* --- swap --------------------------------------------------------------- */

  /* Restoring the size means moving BOTH the radio and the label's is-active
     class. The section's forceVisibleSizeIntoRealVariant() reads the label, not
     the input, and slams every radio to match it on a timeout after re-init —
     so setting the input alone gets silently reverted to the server default. */
  function restoreSize(scope, size) {
    if (!size) return false;

    var wanted = String(size);
    var inputs = scope.querySelectorAll('.elio-pdp__size-input');
    var target = null;
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].value === wanted) { target = inputs[i]; break; }
    }
    if (!target || target.disabled) return false;

    /* Labels are matched by walking them rather than querying with an escaped
       id: section ids contain characters that need CSS.escape, and a throw here
       would reject the swap promise and trigger a full page load. */
    var labels = scope.querySelectorAll('label.elio-pdp__size');
    for (var j = 0; j < inputs.length; j++) {
      inputs[j].checked = inputs[j] === target;
    }
    for (var k = 0; k < labels.length; k++) {
      labels[k].classList.toggle('is-active', labels[k].getAttribute('for') === target.id);
    }
    return true;
  }

  /* Post-swap integrity check. A re-executed script that throws surfaces as an
     uncaught window error, not a rejected promise, so a swap can "succeed" and
     still leave a form wired to a stale or missing variant. Adding the wrong
     item to cart is the worst failure available here, so if the form does not
     verify against the newly rendered variant list, force a real navigation. */
  function verify(scope) {
    var node = scope.querySelector('[data-product-variants]');
    if (!node) return 'variant list missing';

    var variants;
    try { variants = JSON.parse(node.textContent || '[]'); } catch (e) { return 'variant list unparseable'; }
    if (!variants.length) return 'variant list empty';

    var input = scope.querySelector('form [name="id"], input[name="id"]');
    if (!input) return 'variant input missing';
    if (!input.value) return 'variant input empty';

    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(input.value)) return null;
    }
    return 'variant ' + input.value + ' not in this colorway';
  }

  function refocusSwatch(scope, baseUrl) {
    var chip = scope.querySelector('[data-color-link][data-base-url="' + baseUrl + '"]');
    if (chip && typeof chip.focus === 'function') chip.focus({ preventScroll: true });
  }

  function swap(url, baseUrl, push) {
    var host = wrapper();
    if (!host) { hardNavigate(url); return; }

    var size = selectedSize();
    var warm = !!cache[stripQuery(url)];
    busy = true;
    host.setAttribute('aria-busy', 'true');

    /* A warm swap paints from memory in a frame or two. Dimming it would only
       invent latency the shopper would otherwise never see, so the pending
       state is delayed and cancelled the moment the swap lands. */
    var dimTimer = null;
    if (!warm && !reduceMotion) {
      dimTimer = setTimeout(function () { host.classList.add('elio-pdp-swapping'); }, 120);
    }

    load(url)
      .then(function (payload) {
        // Scroll position is deliberately preserved. Jumping to the top is what
        // makes a swap feel like a navigation.
        /* Replacing innerHTML destroys the children but leaves the wrapper's
           OWN attributes intact — including any run-once init guard a script
           set on it. The carousel keys off data-elio-carousel-ready on this
           element, so leaving it set makes its init return before binding the
           new thumbnails and arrows. Clear every elio init flag on the wrapper
           before the new markup lands. */
        Array.prototype.slice.call(host.attributes).forEach(function (attr) {
          if (/^data-elio-.*-ready$/.test(attr.name)) host.removeAttribute(attr.name);
        });
        host.removeAttribute('data-elio-carousel-ready');
Array.prototype.slice.call(host.attributes).forEach(function (attr) {
          if (/^data-elio-.*-ready$/.test(attr.name)) host.removeAttribute(attr.name);
        });
        host.removeAttribute('data-elio-carousel-ready');
        host.innerHTML = payload.html;

        /* Size is restored BEFORE re-init, so the section's scripts read the
           shopper's size as the DOM truth from their first pass rather than
           booting on the server default and being corrected afterwards. */
        var sizeHeld = restoreSize(host, size);

        reinit(host);

        /* Re-assert after re-init, then announce it, because the engine's own
           deferred passes run on a timeout and a frame. */
        if (sizeHeld) {
          restoreSize(host, size);
          nextFrame(function () {
            if (!restoreSize(host, size)) return;
            var sel = host.querySelector('.elio-pdp__size-input:checked');
            if (sel) sel.dispatchEvent(new Event('change', { bubbles: true }));
          });
          setTimeout(function () { restoreSize(host, size); }, 30);
        }

        /* Verification runs BEFORE the URL is pushed. If it ran after, the
           browser would already be sitting on the target URL and assigning
           location.href to it would be a same-document no-op — the forced
           reload would silently fail and leave exactly the broken state this
           guard exists to prevent. */
        var problem = verify(host);
        if (problem) {
          if (window.console && console.warn) console.warn('[elio] PDP swap failed verification: ' + problem);
          hardNavigate(stripQuery(url));
          return;
        }

        syncHead(payload.meta, stripQuery(url));
        refocusSwatch(host, baseUrl);

        var target = stripQuery(url) + (size ? '?size=' + encodeURIComponent(size) : '');
        if (push) history.pushState({ elioPdp: true, base: baseUrl }, '', target);

        /* Belt and braces on the URL. The theme's variant selector rewrites to
           "?variant=<id>" via replaceState whenever a change event reaches it;
           data-update-url="false" on <sht-variant-radios> turns that off, but a
           third-party script or a future theme update could reintroduce it, and
           the shopper would see an opaque id instead of ?size=. Re-assert once
           the deferred passes have settled if anything moved it. */
        setTimeout(function () {
          if (window.location.pathname + window.location.search !== target) {
            try { history.replaceState(history.state, '', target); } catch (e) {}
          }
        }, 60);

        // If the previously selected size is sold out in this colorway the
        // server default stands, which is correct but silent. Say so.
        var landed = selectedSize();
        if (size && landed && landed !== size && window.elioA11y) {
          window.elioA11y.announce('Size ' + size + ' unavailable in this color. Size ' + landed + ' selected.');
        }

        trackView(host);

        var label = host.querySelector('[data-current-color]');
        if (window.elioA11y && label) {
          window.elioA11y.announce(label.textContent.trim() + ' selected');
        }

        document.dispatchEvent(new CustomEvent('elio:pdp:swapped', {
          detail: { url: stripQuery(url), sectionId: SECTION_ID, size: size }
        }));
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Any failure hands control back to the browser. The shopper still gets there.
        hardNavigate(url);
      })
      .finally(function () {
        busy = false;
        if (dimTimer) clearTimeout(dimTimer);
        var h = wrapper();
        if (h) { h.removeAttribute('aria-busy'); h.classList.remove('elio-pdp-swapping'); }
      });
  }

  /* --- events ------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-color-link]');
    if (!link) return;
    if (!wrapper() || !wrapper().contains(link)) return;

    // Leave every non-plain click to the browser.
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.target && link.target !== '_self') return;

    var base = link.getAttribute('data-base-url');
    var href = link.getAttribute('href') || base;
    if (!base || !href) return;

    /* Already on this colourway. No swap to run, but the size may have changed
       since the URL was written, so re-sync it. replaceState rather than push:
       clicking the colour you are already on should not add a history entry. */
    if (base === activeColorKey()) {
      e.preventDefault();
      var current = selectedSize();
      var here = stripQuery(href) + (current ? '?size=' + encodeURIComponent(current) : '');
      if (window.location.pathname + window.location.search !== here) {
        try { history.replaceState(history.state, '', here); } catch (err) {}
      }
      return;
    }
    if (busy) { e.preventDefault(); return; }

    e.preventDefault();
    swap(href, base, true);
  }, false);

  // Warm the cache before the click lands.
  ['pointerenter', 'touchstart', 'focusin'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var link = e.target.closest && e.target.closest('[data-color-link]');
      if (!link) return;
      var base = link.getAttribute('data-base-url');
      if (base && base !== activeColorKey()) prefetch(base);
    }, { capture: true, passive: true });
  });

  /* ---- keep the URL on the selected size --------------------------------
     The theme's own variant URL writer is switched off (data-update-url="false"
     on <sht-variant-radios>) because it writes an opaque "?variant=<id>". This
     keeps ELIO's convention instead: /products/<handle>?size=<size>, updated in
     place with no reload. Delegated and idempotent, so it survives the section
     being swapped out from under it. */
  function syncSizeInUrl() {
    if (busy) return;                       // a swap is mid-flight and owns the URL
    var host = wrapper();
    if (!host) return;

    var size = selectedSize();
    var target = stripQuery(window.location.pathname) +
                 (size ? '?size=' + encodeURIComponent(size) : '');

    if (window.location.pathname + window.location.search === target) return;
    try { history.replaceState(history.state, '', target); } catch (e) {}
  }

  ['change', 'click'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var hit = e.target.closest &&
        e.target.closest('.elio-pdp__size, .elio-pdp__size-input, label[for^="' + SECTION_ID + '"]');
      if (!hit) return;
      var host = wrapper();
      if (!host || !host.contains(hit)) return;
      /* Deferred: the section's own handlers move the checked radio and the
         is-active label on this same event, so reading the size synchronously
         here would capture the size being replaced rather than the new one. */
      nextFrame(syncSizeInUrl);
      setTimeout(syncSizeInUrl, 40);
    }, true);
  });

  window.addEventListener('popstate', function (e) {
    if (!e.state || !e.state.elioPdp) return;
    swap(window.location.pathname + window.location.search, e.state.base || '', false);
  });

  if (document.readyState === 'complete') {
    warmAll();
  } else {
    window.addEventListener('load', warmAll, { once: true });
  }

  // Seed history so the first Back returns to the entry colorway rather than
  // leaving the site.
  if (!history.state || !history.state.elioPdp) {
    try {
      history.replaceState(
        { elioPdp: true, base: stripQuery(window.location.pathname) },
        '',
        window.location.href
      );
    } catch (e) {}
  }
})();
